import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Editor } from '@tiptap/react';
import { nip19 } from 'nostr-tools';
import { useSeoMeta } from '@unhead/react';
import {
  ArrowLeft,
  Copy,
  FileText,
  Lock,
  MoreHorizontal,
  Printer,
  Share2,
  Trash2,
  UserRoundPlus,
} from 'lucide-react';

import { DocEditor } from '@/components/editor/DocEditor';
import { EditorToolbar } from '@/components/editor/EditorToolbar';
import { PresenceAvatars } from '@/components/editor/PresenceAvatars';
import { ShareDialog } from '@/components/editor/ShareDialog';
import { SyncStatusBadge } from '@/components/editor/SyncStatusBadge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/useToast';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useDocActions } from '@/hooks/useDocActions';
import { useDocSync } from '@/hooks/useDocSync';
import { colorForPubkey, formatRelativeTime, shortPubkey } from '@/lib/identity';

export default function DocEditorPage() {
  const { npub, docId } = useParams<{ npub: string; docId: string }>();

  const ownerPubkey = useMemo(() => {
    if (!npub) return undefined;
    try {
      const decoded = nip19.decode(npub);
      return decoded.type === 'npub' ? decoded.data : undefined;
    } catch {
      return undefined;
    }
  }, [npub]);

  if (!ownerPubkey || !docId || !/^[0-9a-f]{4,64}$/.test(docId)) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-8 text-center">
        <p className="text-lg font-semibold">Document not found</p>
        <Link to="/" className="text-sm text-muted-foreground underline">Back to documents</Link>
      </div>
    );
  }

  return <DocEditorScreen key={`${ownerPubkey}:${docId}`} ownerPubkey={ownerPubkey} docId={docId} />;
}

function DocEditorScreen({ ownerPubkey, docId }: { ownerPubkey: string; docId: string }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const { ydoc, awareness, provider, state, presenceUsers } = useDocSync(ownerPubkey, docId);
  const owner = useAuthor(ownerPubkey);
  const actions = useDocActions();

  const [editor, setEditor] = useState<Editor | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [title, setTitle] = useState(() => state.metadata?.title ?? '');
  const [wordCount, setWordCount] = useState(0);
  const titleRef = useRef<HTMLInputElement>(null);
  const lastTypedAt = useRef(0);

  const metadata = state.metadata;
  const isOwner = user?.pubkey === ownerPubkey;
  const canEdit = state.canEdit;
  const titleForMeta = metadata?.title || 'Untitled document';

  useSeoMeta({
    title: () => `${titleForMeta} — Nostr Office`,
  });

  // Follow external title changes (e.g. the owner renames in another tab)
  // unless the local field is focused.
  useEffect(() => {
    if (document.activeElement !== titleRef.current) {
      setTitle(metadata?.title ?? '');
    }
  }, [metadata?.title]);

  // Word count (debounced — cheap and keeps the footer informative).
  useEffect(() => {
    if (!editor) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const update = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const text = editor.getText().trim();
        setWordCount(text ? text.split(/\s+/).length : 0);
      }, 400);
    };
    update();
    editor.on('update', update);
    return () => {
      editor.off('update', update);
      if (timer) clearTimeout(timer);
    };
  }, [editor]);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    lastTypedAt.current = Date.now();
    provider.setTitle(value);
  };

  const shareUrl = `${location.origin}/doc/${nip19.npubEncode(ownerPubkey)}/${docId}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({ title: 'Link copied to clipboard' });
    } catch {
      toast({ title: 'Could not copy the link', variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    const ok = await actions.deleteDocument({
      ownerPubkey,
      docId,
      title: titleForMeta,
      updatedAt: Math.floor(Date.now() / 1000),
      createdAt: metadata?.createdAt ?? Math.floor(Date.now() / 1000),
      deleted: false,
      collaboratorPubkeys: metadata?.collaborators.map((c) => c.pubkey) ?? [],
      role: 'owner',
    });
    if (ok) {
      toast({ title: 'Document deleted' });
      navigate('/');
    } else {
      toast({ title: 'Could not delete the document', variant: 'destructive' });
    }
  };

  const handleDuplicate = async () => {
    const copy = await actions.duplicateDocument({
      ownerPubkey,
      docId,
      title: titleForMeta,
      updatedAt: Math.floor(Date.now() / 1000),
      createdAt: metadata?.createdAt ?? 0,
      deleted: false,
      collaboratorPubkeys: [],
      role: isOwner ? 'owner' : 'viewer',
    });
    if (copy) {
      toast({ title: 'Duplicated', description: 'Opening the copy…' });
      navigate(`/doc/${nip19.npubEncode(copy.ownerPubkey)}/${copy.docId}`);
    } else {
      toast({ title: 'Could not duplicate the document', variant: 'destructive' });
    }
  };

  const ownerName = owner.data?.metadata?.display_name
    || owner.data?.metadata?.name
    || shortPubkey(ownerPubkey);

  return (
    <div className="flex h-dvh flex-col bg-background print:h-auto print:overflow-visible">
      {/* Top bar */}
      <header className="flex h-14 shrink-0 items-center gap-1.5 border-b bg-background px-2 sm:gap-2 sm:px-4 print:hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/')}
          aria-label="Back to documents"
          className="h-9 w-9 shrink-0"
        >
          <ArrowLeft className="h-4.5 w-4.5" />
        </Button>

        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground"
          aria-hidden
        >
          <FileText className="h-4.5 w-4.5" />
        </div>

        <div className="min-w-0 flex-1">
          <input
            ref={titleRef}
            value={title}
            onChange={(event) => handleTitleChange(event.target.value)}
            readOnly={!isOwner}
            placeholder="Untitled document"
            aria-label="Document title"
            className="w-full truncate rounded-md border border-transparent bg-transparent px-1.5 py-0.5 text-sm font-semibold outline-none transition-colors hover:border-border focus:border-ring sm:text-[15px]"
          />
          <p className="hidden truncate px-1.5 text-xs text-muted-foreground sm:block">
            {isOwner ? 'Owned by you' : `Shared by ${ownerName}`}
            {metadata ? ` · edited ${formatRelativeTime(metadata.updatedAt)}` : ''}
          </p>
        </div>

        <PresenceAvatars users={presenceUsers} className="mx-1 hidden sm:flex" />

        <SyncStatusBadge status={state.status} className="shrink-0" />

        <Button
          variant="outline"
          size="sm"
          className="h-9 shrink-0 gap-1.5 px-2.5 sm:px-3.5"
          onClick={() => setShareOpen(true)}
        >
          <UserRoundPlus className="h-4 w-4 sm:hidden" />
          <Share2 className="hidden h-4 w-4 sm:block" />
          <span className="hidden sm:inline">Share</span>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" aria-label="More actions">
              <MoreHorizontal className="h-4.5 w-4.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={() => void copyLink()}>
              <Copy className="h-4 w-4" /> Copy link
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => window.print()}>
              <Printer className="h-4 w-4" /> Print / Save as PDF
            </DropdownMenuItem>
            {user && (
              <DropdownMenuItem onClick={() => void handleDuplicate()}>
                <Copy className="h-4 w-4" /> Duplicate
              </DropdownMenuItem>
            )}
            {isOwner && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setDeleteOpen(true)}>
                  <Trash2 className="h-4 w-4" /> Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Toolbar or view-only notice */}
      {canEdit ? (
        <EditorToolbar editor={editor} className="sticky top-0 z-10 shrink-0 print:hidden" />
      ) : (
        <div className="flex shrink-0 items-center justify-center gap-2 border-b bg-muted/50 px-4 py-2 text-sm text-muted-foreground print:hidden">
          <Lock className="h-4 w-4" aria-hidden />
          {user
            ? 'You have view access — this document is read-only for you.'
            : 'View mode — log in with Nostr to edit shared documents.'}
        </div>
      )}

      {/* Document canvas */}
      <main className="doc-scroll flex-1 overflow-y-auto overscroll-contain bg-muted/40 dark:bg-background print:overflow-visible">
        <div className="mx-auto w-full max-w-[880px] px-0 pb-24 pt-0 sm:px-8 sm:py-10 print:max-w-none print:p-0">
          <div className="min-h-[calc(100dvh-7rem)] bg-card px-5 py-8 text-foreground sm:min-h-[calc(100dvh-9rem)] sm:rounded-xl sm:border sm:px-14 sm:py-14 sm:shadow-sm print:min-h-0 print:rounded-none print:border-0 print:p-0 print:shadow-none">
            <DocEditor
              ydoc={ydoc}
              awareness={awareness}
              editable={canEdit}
              onEditor={setEditor}
              userName={user?.pubkey
                ? shortPubkey(user.pubkey)
                : 'Guest'}
              userColor={user?.pubkey ? colorForPubkey(user.pubkey) : '#64748b'}
            />
          </div>

          <footer className="flex flex-col items-center gap-1 px-6 py-6 text-center text-xs text-muted-foreground print:hidden">
            <p>
              {wordCount === 1 ? '1 word' : `${wordCount} words`} · synced over Nostr relays
            </p>
            <p>
              This MVP stores documents unencrypted on public relays —{' '}
              <span className="font-medium">end-to-end encryption is on the roadmap</span>.
            </p>
            <p>
              Vibed with{' '}
              <a
                href="https://shakespeare.diy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-foreground"
              >
                Shakespeare
              </a>
            </p>
          </footer>
        </div>
      </main>

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        metadata={metadata}
        provider={provider}
        isOwner={isOwner}
        shareUrl={shareUrl}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>
              “{titleForMeta}” will be removed from your documents and a deletion request
              will be broadcast to relays. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void handleDelete()}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
